import { useEffect, useState, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'

import { useSession } from 'next-auth/react'
import { useRouter } from 'next/router'

import NavHeightContext from '@context/NavHeightContext'

import useBrazeIntegration from '@context/useBrazeIntegration'

import { Footer, Nav } from '@components/global'
import CookieSettings from '@components/global/CookieSettings'
import Toasts from '@components/global/ToastView'

type MainLayoutProps = {
	children: ReactNode
}

export default function MainLayout({ children }: MainLayoutProps) {
	// Open braze session when user is detected
	const { data: session } = useSession()
	const router = useRouter()
	const [brazeSessionOpened, setBrazeSessionOpened] = useState(false)
	const { brazeFunction, getBrazeExternalId } = useBrazeIntegration()

	const [navHeight, setNavHeight] = useState(0)
	const heightContextValue = useMemo(() => ({ navHeight, setNavHeight }), [navHeight, setNavHeight])

	// Initialize Braze session on first load
	useEffect(() => {
		if (!brazeSessionOpened) {
			;(async () => {
				const braze = await brazeFunction()

				if (braze) {
					// Get the appropriate external_id (Shopify Customer ID or email for anonymous users)
					const externalId = getBrazeExternalId(session)
					
					// Set the user if we have an external_id
					if (externalId) {
						braze.changeUser(externalId)
						
						// Set email if available
						if (session?.user?.customer?.email || session?.user?.email) {
							braze.getUser()?.setEmail(session.user.customer?.email || session.user.email)
						}
					}
					
					braze.openSession()
					setBrazeSessionOpened(true)
					
					// Log initial page view
					await logPageView(braze, router.pathname)
				}
			})()
		}
	}, [brazeSessionOpened, session, brazeFunction, getBrazeExternalId, router.pathname])

	// Track route changes for page view events
	useEffect(() => {
		if (brazeSessionOpened) {
			;(async () => {
				const braze = await brazeFunction()
				if (braze) {
					await logPageView(braze, router.pathname)
				}
			})()
		}
	}, [router.pathname, brazeSessionOpened, brazeFunction])

	// Helper function to log page views
	const logPageView = async (braze: any, pathname: string) => {
		if (pathname === '/subscriptions/signature-box') {
			braze.logCustomEvent('view_signature_box_page')
		} else {
			braze.logCustomEvent('view_default_page')
		}
	}

	const isHomepage = router.pathname === '/'
	const isFullPageFlow = router.pathname === '/get-started'

	let paddingTop = `${navHeight}px`
	if (isFullPageFlow) {
		paddingTop = '0px'
	} else if (isHomepage) {
		paddingTop = '40px'
	}

	return (
		<NavHeightContext.Provider value={heightContextValue}>
			{!isFullPageFlow && <Nav />}

			<main style={{ paddingTop }}>
				{children}
			</main>

			{!isFullPageFlow && <Footer />}

			<Toasts />

			{!isFullPageFlow && <CookieSettings />}
		</NavHeightContext.Provider>
	)
}
